import { processLegalMarkdown } from './legal-markdown-processor';
/**
 * Compiles a document instance by merging template with form values and signatures
 */
export function compileDocument(templateContent, documentInstance, fields, options = {}) {
    const { includeSignatures = true, includeImages = true, format = 'markdown' } = options;
    // Merge values and signatures
    const mergedValues = { ...documentInstance.values };
    // Add signature images to values if including signatures
    if (includeSignatures) {
        Object.entries(documentInstance.signatures).forEach(([fieldName, signatureData]) => {
            mergedValues[fieldName] = signatureData.data; // Base64 image data
        });
    }
    // Process the template with merged values
    let compiledContent = processLegalMarkdown(templateContent, mergedValues);
    // Add signature metadata section if requested
    if (includeSignatures && Object.keys(documentInstance.signatures).length > 0) {
        compiledContent += '\n\n---\n\n## Signature Verification\n\n';
        Object.entries(documentInstance.signatures).forEach(([fieldName, signatureData]) => {
            const field = fields.find(f => f.variableName === fieldName);
            const fieldLabel = field?.label || fieldName;
            compiledContent += `\n**${fieldLabel}**\n`;
            compiledContent += `- Signed by: ${signatureData.signedBy}\n`;
            compiledContent += `- Signed at: ${new Date(signatureData.signedAt).toLocaleString()}\n`;
            compiledContent += `- Type: ${signatureData.type}\n`;
            if (signatureData.ipAddress) {
                compiledContent += `- IP Address: ${signatureData.ipAddress}\n`;
            }
        });
    }
    // Calculate completion percentage
    const requiredFields = fields.filter(f => f.validation?.required);
    const filledFields = requiredFields.filter(f => {
        const value = mergedValues[f.variableName];
        return value !== undefined && value !== null && value !== '';
    });
    const completionPercentage = requiredFields.length > 0
        ? Math.round((filledFields.length / requiredFields.length) * 100)
        : 100;
    // Convert to HTML if requested
    let outputContent = compiledContent;
    if (format === 'html') {
        outputContent = markdownToHtml(compiledContent);
    }
    return {
        content: outputContent,
        format,
        generatedAt: new Date(),
        signatures: documentInstance.signatures,
        completionPercentage
    };
}
/**
 * Simple markdown to HTML converter
 * For production, consider using a library like marked or remark
 */
function markdownToHtml(markdown) {
    let html = markdown;
    // Headers
    html = html.replace(/^### (.*$)/gim, '<h3>$1</h3>');
    html = html.replace(/^## (.*$)/gim, '<h2>$1</h2>');
    html = html.replace(/^# (.*$)/gim, '<h1>$1</h1>');
    // Bold
    html = html.replace(/\*\*(.*?)\*\*/gim, '<strong>$1</strong>');
    // Italic
    html = html.replace(/\*(.*?)\*/gim, '<em>$1</em>');
    // Links
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/gim, '<a href="$2">$1</a>');
    // Line breaks
    html = html.replace(/\n\n/g, '</p><p>');
    html = html.replace(/\n/g, '<br>');
    // Images (signatures)
    html = html.replace(/!\[([^\]]*)\]\(([^)]+)\)/gim, '<img src="$2" alt="$1" style="max-height: 100px;">');
    // Horizontal rules
    html = html.replace(/^---$/gim, '<hr>');
    // Lists
    html = html.replace(/^\* (.+)$/gim, '<li>$1</li>');
    html = html.replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>');
    // Wrap in paragraphs
    html = '<p>' + html + '</p>';
    return html;
}
/**
 * Validates that all required fields are filled
 */
export function validateDocument(documentInstance, fields) {
    const requiredFields = fields.filter(f => f.validation?.required);
    const missingFields = requiredFields.filter(field => {
        const value = documentInstance.values[field.variableName];
        // Check if signature field has signature
        if (field.fieldType === 'signature') {
            return !documentInstance.signatures[field.variableName];
        }
        // Check if value exists and is not empty
        return !value || (typeof value === 'string' && value.trim() === '');
    });
    return {
        valid: missingFields.length === 0,
        missingFields
    };
}
/**
 * Generates a print-optimized HTML version with embedded CSS
 */
export function generatePrintableHtml(compiledContent, documentTitle, includeWatermark = false) {
    const watermarkCss = includeWatermark ? `
    .watermark {
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%) rotate(-45deg);
      font-size: 120px;
      opacity: 0.1;
      z-index: -1;
      pointer-events: none;
    }
  ` : '';
    const watermarkHtml = includeWatermark ? '<div class="watermark">DRAFT</div>' : '';
    return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${documentTitle}</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700&family=Montserrat:wght@300;400;500;600&display=swap');

    @page {
      size: A4;
      margin: 2.5cm 2cm;
    }

    * {
      box-sizing: border-box;
    }

    body {
      font-family: 'Montserrat', sans-serif;
      font-size: 11pt;
      line-height: 1.6;
      color: #1a1a1a;
      max-width: 8.5in;
      margin: 0 auto;
      padding: 1in;
      background: white;
    }

    h1, h2, h3, h4, h5, h6 {
      font-family: 'Playfair Display', serif;
      font-weight: 700;
      page-break-after: avoid;
      margin-top: 1.5em;
      margin-bottom: 0.75em;
    }

    h1 {
      font-size: 28pt;
      text-align: center;
      border-bottom: 3px solid #d97706;
      padding-bottom: 0.5em;
      margin-bottom: 1em;
    }

    h2 {
      font-size: 18pt;
      border-bottom: 1px solid #e5e7eb;
      padding-bottom: 0.25em;
    }

    h3 {
      font-size: 14pt;
    }

    p {
      margin: 0.75em 0;
      text-align: justify;
      hyphens: auto;
    }

    strong {
      font-weight: 600;
    }

    ul, ol {
      margin: 0.5em 0;
      padding-left: 2em;
    }

    li {
      margin: 0.25em 0;
    }

    hr {
      border: none;
      border-top: 1px solid #d1d5db;
      margin: 2em 0;
    }

    img {
      max-width: 100%;
      height: auto;
      display: block;
      margin: 1em 0;
      page-break-inside: avoid;
    }

    table {
      width: 100%;
      border-collapse: collapse;
      margin: 1em 0;
    }

    th, td {
      border: 1px solid #d1d5db;
      padding: 0.5em;
      text-align: left;
    }

    th {
      background-color: #f9fafb;
      font-weight: 600;
    }

    .signature-section {
      margin-top: 3em;
      padding-top: 1em;
      border-top: 2px solid #d1d5db;
    }

    .signature-box {
      border: 1px solid #d1d5db;
      padding: 1em;
      margin: 1em 0;
      background: #f9fafb;
    }

    ${watermarkCss}

    @media print {
      body {
        padding: 0;
      }

      .no-print {
        display: none !important;
      }

      h1, h2, h3, h4, h5, h6 {
        page-break-after: avoid;
      }

      p, ul, ol, table {
        page-break-inside: avoid;
      }
    }
  </style>
</head>
<body>
  ${watermarkHtml}
  ${compiledContent}

  <div class="no-print" style="margin-top: 3em; padding: 1em; background: #f9fafb; border-radius: 8px; text-align: center;">
    <p style="margin: 0; color: #6b7280; font-size: 10pt;">
      Generated on ${new Date().toLocaleString()}
    </p>
  </div>
</body>
</html>
  `.trim();
}
/**
 * Downloads compiled document as markdown file
 */
export function downloadAsMarkdown(content, filename) {
    const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}.md`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}
/**
 * Downloads compiled document as HTML file
 */
export function downloadAsHtml(htmlContent, filename) {
    const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}
/**
 * Opens document in new window for printing
 */
export function printDocument(htmlContent) {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
        alert('Please allow popups to print the document');
        return;
    }
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    // Wait for content to load before printing
    printWindow.onload = () => {
        printWindow.print();
    };
}
/**
 * Converts HTML document to PDF and triggers download
 * Uses html2pdf.js library for client-side conversion
 */
export async function downloadAsPdf(htmlContent, filename) {
    try {
        // Dynamically import html2pdf to avoid bundle issues
        const { default: html2pdf } = await import('html2pdf.js');
        const element = document.createElement('div');
        element.innerHTML = htmlContent;
        const opt = {
            margin: [10, 10, 10, 10], // mm
            filename: `${filename}.pdf`,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 2, logging: false },
            jsPDF: { orientation: 'portrait', unit: 'mm', format: 'a4' }
        };
        await html2pdf().set(opt).from(element).save();
    }
    catch (error) {
        console.error('Error generating PDF:', error);
        throw new Error('Failed to generate PDF. Please try again.');
    }
}
/**
 * Converts HTML document to PDF Blob (for uploading to storage)
 * Returns the PDF as a Blob for programmatic use
 */
export async function htmlToPdfBlob(htmlContent, filename) {
    try {
        const { default: html2pdf } = await import('html2pdf.js');
        const element = document.createElement('div');
        element.innerHTML = htmlContent;
        const opt = {
            margin: [10, 10, 10, 10],
            filename: `${filename}.pdf`,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 2, logging: false },
            jsPDF: { orientation: 'portrait', unit: 'mm', format: 'a4' }
        };
        // Generate PDF as blob instead of auto-downloading
        return new Promise((resolve, reject) => {
            html2pdf()
                .set(opt)
                .from(element)
                .toPdf()
                .get('pdf')
                .then((pdf) => {
                const blob = pdf.output('blob');
                resolve(blob);
            })
                .catch(reject);
        });
    }
    catch (error) {
        console.error('Error converting HTML to PDF:', error);
        throw new Error('Failed to convert document to PDF');
    }
}
